// TES-1034 S8 (Emelie, point 6) — how the transfer review states the
// patient's subscription choice. Kirill sees her choice, with the option and
// price, in the review and in the positive decision; he neither chooses nor
// approves it. The price comes from the tenant's pricing (the same
// subscription-plans read as the link preview), never from the copy.
//
// Pure on purpose: no React, no path aliases (`node --test`).
import {
  TRANSFER_PLAN_UNCLEAR_REASON_LABEL,
  transferPlanOptionLabel,
  type SubscriptionPlan,
  type TransferPlanChoiceView,
} from "@care-by-ella/contracts";

export type PlanAmounts = Partial<Record<SubscriptionPlan, number | null>>;

export function planLabel(
  plan: SubscriptionPlan,
  amounts: PlanAmounts,
): string {
  return transferPlanOptionLabel(plan, amounts[plan] ?? null);
}

/** "Patientens val: Utan personlig coach – 695 kr/mån". */
export function patientChoiceLine(
  view: TransferPlanChoiceView,
  amounts: PlanAmounts,
): string {
  return view.patientPlan
    ? `Patientens val: ${planLabel(view.patientPlan, amounts)}`
    : "Patientens val: inget val i hennes version av formuläret";
}

/** Why her eligibility for the option without a coach is unclear, or null. */
export function eligibilityLine(view: TransferPlanChoiceView): string | null {
  if (!view.eligibility || view.eligibility.status === "eligible") return null;
  const why = view.eligibility.reasons
    .map((r) => TRANSFER_PLAN_UNCLEAR_REASON_LABEL[r])
    .join(", ");
  return `Behörighet för alternativet utan coach: oklar (${why}). Endast alternativet med personlig coach erbjöds.`;
}

/** What the link after an approval is sent for, as the dialog states it. */
export function sentPlanLine(
  view: TransferPlanChoiceView,
  amounts: PlanAmounts,
): string {
  const base = `Skickas till patienten: ${planLabel(view.effectivePlan, amounts)}`;
  return view.correction ? `${base} (korrigerat av läkare)` : base;
}

/**
 * TES-1055 (Emelie, point J) — "Skicka abonnemangslänken igen" for a transfer
 * patient starts from the plan she is actually sent: her F-07 choice, or the
 * doctor's logged correction (`effectivePlan`), so the doctor does not choose
 * the plan again. "Ella Underhåll" carries the same basis the automatic link
 * after the approval records (TransferReviewService.linkChoiceFor), which he
 * can still edit. Null when her F-07 did not ask: then nothing is preselected
 * (TES-907, Ö-221).
 */
export function resendPlanPrefill(
  view: TransferPlanChoiceView | null | undefined,
): { plan: SubscriptionPlan; basis: string } | null {
  if (!view) return null;
  const plan = view.effectivePlan;
  if (plan !== "ella_underhall") return { plan, basis: "" };
  const basis = view.correction
    ? `Läkarens korrigering av patientens val: ${view.correction.reason}`.trim()
    : "Patientens eget val i receptövertaget (F-07): minst tolv månaders behandling sammanlagt.";
  return { plan, basis };
}
